import type { ConfigOptions } from '@/config-hoc/types';
import {
  buildMobileAppHref,
  getCurrentMobileRouteContext,
  parseMobileRoute,
} from '@/config/tenant-route';
import type { TenantEntity } from '@ts-max/car-export-api-sdk';
import type { PermissionCode } from '@ts-max/car-export-mobile-permission-code-sdk';
import { Storage } from '@ts-max/utils';
import type { AxiosResponse } from 'axios';
import { match } from 'path-to-regexp';
import pagesConfig, { type PageConfig as BasePageConfig } from '~pages-config';

const AUTH_STATE_CHANGE_EVENT = 'car-export-mobile:auth-state-change';
const TENANT_STATE_CHANGE_EVENT = 'car-export-mobile:tenant-state-change';

export interface LoginUser {
  id: number | string;
  name: string;
  accessToken?: string;
  verified?: boolean;

  [key: string]: any;
}

export type PageConfig = ConfigOptions & BasePageConfig;

export const storage = {
  session: new Storage({ storage: sessionStorage }),
  local: new Storage({ storage: localStorage }),
};

export function getCurrentTenantCode(): string {
  return getCurrentMobileRouteContext().tenantCode;
}

export function getPageConfig(
  pathname: string = window.location.pathname,
): PageConfig {
  const routePath = parseMobileRoute(pathname).routePath;

  const cfg = pagesConfig?.find((item) => {
    if (!item.routePath) return false;
    return match(item.routePath)(routePath);
  });

  return (cfg || {}) as PageConfig;
}

export function getToken(): string {
  return storage.session.getItem('accessToken') || '';
}

export function setToken(token: string) {
  storage.session.setItem('accessToken', token || null);
}

export function getLoginUser(): LoginUser | null {
  return storage.local.getItem('loginUser') || null;
}

export function setLoginUser(user: LoginUser | null) {
  storage.local.setItem('loginUser', user);
  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

export function isLogin() {
  return !!getLoginUser();
}

export function getPermission(): string[] {
  return storage.session.getItem('permissions') || [];
}

export function setPermission(permissions: string[]) {
  storage.session.setItem('permissions', permissions);
  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

export function hasPermission(
  permission?: PermissionCode | PermissionCode[] | boolean,
) {
  if (permission === undefined || permission === true) return true;
  if (permission === false) return false;

  const permissions = getPermission();
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some((code) => permissions.includes(code));
}

export function subscribeAuthStateChange(listener: () => void) {
  window.addEventListener(AUTH_STATE_CHANGE_EVENT, listener);
  return () => window.removeEventListener(AUTH_STATE_CHANGE_EVENT, listener);
}

export function getCurrentTenant(): TenantEntity {
  return (
    storage.session.getItem('currentTenant') ||
    ({
      id: 0,
      code: getCurrentTenantCode(),
      name: '汽车出口移动端',
    } as TenantEntity)
  );
}

export function setCurrentTenant(tenant: TenantEntity) {
  storage.session.setItem('currentTenant', tenant);
  window.dispatchEvent(new Event(TENANT_STATE_CHANGE_EVENT));
}

export function subscribeTenantStateChange(listener: () => void) {
  window.addEventListener(TENANT_STATE_CHANGE_EVENT, listener);
  return () => window.removeEventListener(TENANT_STATE_CHANGE_EVENT, listener);
}

function validateSameOriginRedirect(
  redirect: string | undefined | null,
): string | null {
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.href;
  } catch {
    return null;
  }
}

function isLoginPath(path = window.location.pathname) {
  let pathname = path;
  try {
    pathname = new URL(path, window.location.origin).pathname;
  } catch {
    pathname = path;
  }
  return (
    pathname === withBasename('/login') ||
    pathname.startsWith(`${withBasename('/login')}/`)
  );
}

function withBasename(path: string) {
  return buildMobileAppHref(path);
}

export function toLogin(options?: {
  response?: AxiosResponse;
  redirect?: string;
}) {
  if (isLoginPath()) return;

  const loginPath = withBasename('/login');
  const redirect =
    validateSameOriginRedirect(options?.redirect) ?? window.location.href;

  setLoginUser(null);
  setToken('');
  setPermission([]);
  window.location.href = `${loginPath}?redirect=${encodeURIComponent(redirect)}`;
}

export function toHome() {
  window.location.href = withBasename('/');
}

export function toVerify() {
  toHome();
}
